import { put, call, takeEvery, select, all } from "redux-saga/effects";
import _ from 'lodash';
import moment from 'moment-timezone';
import { getJwtToken, getDataFromRDS, getDataFromSxi, getDataFromKpiDash } from "api";
import { PICTREE, ENDPOINT } from "../../constants";

import {
  setPageTopLoader,
  setAllSeasonData,
  setSeasonData,
  setSeasonDataNtt,
  setSeasonDataNttFt,
  setSeasonDataHokuden,
  setSeasonDataCommon,
} from 'actions';
import {
  REGULAR_SEASON_IDS,
} from "../../constants/pictreeRegularSeasonIds";

const AREA_BATCH_SUPPORTED_SEASON_IDS = new Set(
  String(
    process.env.REACT_APP_PICTREE_AREA_BATCH_SUPPORTED_SEASON_IDS ||
      REGULAR_SEASON_IDS.join(",")
  )
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
);
const REGULAR_SEASON_AREA_SNAPSHOT_TABLE =
  process.env.REACT_APP_PICTREE_AREA_SNAPSHOT_TABLE ||
  (process.env.NODE_ENV === "development"
    ? "PicTreeKpiRegularSeasonAreaSnapshotProd"
    : "PicTreeKpiRegularSeasonAreaSnapshotProd");

function parseRdsBodyRows(response) {
  const body = response && response.body ? response.body : "[]";
  const parsed = typeof body === "string" ? JSON.parse(body) : body;
  return Array.isArray(parsed) ? parsed : [];
}

function getCellValue(row, index) {
  const cell = Array.isArray(row) ? row[index] : null;
  if (!cell || typeof cell !== "object") {
    return cell ?? null;
  }
  if (cell.isNull) {
    return null;
  }
  if (Object.prototype.hasOwnProperty.call(cell, "stringValue")) {
    return cell.stringValue;
  }
  if (Object.prototype.hasOwnProperty.call(cell, "longValue")) {
    return cell.longValue;
  }
  if (Object.prototype.hasOwnProperty.call(cell, "doubleValue")) {
    return cell.doubleValue;
  }
  return null;
}

function getFirstRowValue(response) {
  const rows = parseRdsBodyRows(response);
  const firstRow = rows[0];
  if (!Array.isArray(firstRow)) {
    return null;
  }
  return getCellValue(firstRow, 0);
}

export function* handleAllSeasonData() {
  try {
    const jwtToken = yield call(getJwtToken);
    const sqlQueryGetSeason = {
      query: `
        SELECT gs.id           AS 'seasonId'
            ,sn.official_name  AS 'seasonName'
            ,gt.start_datetime AS 'seasonStart'
            ,gt.end_datetime   AS 'seasonEnd'
        FROM game_space gs
        INNER JOIN game_term gt ON gt.id = gs.game_term_id
        INNER JOIN season_name sn ON sn.season_id = gs.id AND sn.language_code = 'JA'
        ORDER BY gt.start_datetime DESC
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const [dataSeasonDataTemp] = yield all([
        call(getDataFromSxi, sqlQueryGetSeason, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
      ]);

    let seasons = [];
    _.each(dataSeasonDataTemp, function(s) {
      const nowJST = moment().tz("Asia/Tokyo");
      const seasonStartJST = moment(s.seasonStart).tz("Asia/Tokyo");

      if (seasonStartJST.isSameOrBefore(nowJST)) {
        const seasonObj = {
          seasonId: s.seasonId,
          seasonName: s.seasonName,
          seasonStart: seasonStartJST.format("YYYY-MM-DD HH:mm"),
          seasonEnd: moment(s.seasonEnd).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm"),
        };
        seasons.push(seasonObj);
      }
    });
    yield put(setAllSeasonData(seasons));
  } catch (error) {
    console.log(error);
  }
}

export function* handleSeasonData() {
  try {
    const jwtToken = yield call(getJwtToken);
    const currentPagePath = yield select((state) => state.page.currentPage);
    const isTepcoPage = String(currentPagePath || "").startsWith(
      "/game/pictree/tepco/"
    );
    const sqlQueryGetSeason = {
      query: `
        SELECT gs.id           AS 'seasonId'
            ,sn.official_name  AS 'seasonName'
            ,gt.start_datetime AS 'seasonStart'
            ,gt.end_datetime   AS 'seasonEnd'
        FROM game_space gs
        INNER JOIN game_term gt ON gt.id = gs.game_term_id
        INNER JOIN season_name sn ON sn.season_id = gs.id AND sn.language_code = 'JA'
        ORDER BY gt.start_datetime DESC
      `,
      database: "KpiDashboard",
      type: "list",
    };
    const [dataSeasonDataTemp] = yield all([
        call(getDataFromSxi, sqlQueryGetSeason, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken),
      ]);

    let seasons = [];
    _.each(dataSeasonDataTemp, function(s) {
      const isExcludedSeasonId =
        s.seasonId === "clxvw56es00005soo28mj4jfs" ||
        s.seasonId === "cm46zidp10001bpp63r3cfhwx" || // akita 1
        s.seasonId === "cm64mqm94000br3n0esu8c0n4" || // kitakanto
        s.seasonId === "cm46zidp30003bpp6cxq101mo" || // akita 2
        s.seasonId === "cmadtlnih0001n3mx1wvl9zog" || // aomori
        s.seasonId === "cmb68qvei00011vo0e591229z" || // hokkaido
        s.seasonId === "cmg7iw3vc00037bowekjy65pc" || // tochigi
        s.seasonId === "cmg7iw3va00017bow596s39cs" || // nagano
        s.seasonId === "cmd5oyfft0001iho10o2iceha" || // iwate
        s.seasonId === "cmkdvml9l0001ngs48zepg7ne" || // tokyo-kana-chiba
        s.seasonId === "cmp4yz2lj00017rtcc4ok0jgv" || // regular-season1
        s.seasonId === "cmkdxxnxn000141s4asb2gdfv" || // okinanwa
        s.seasonId === "cmr0bus0w00010azches0f7ak" || // hokkadio 2026
        s.seasonId === "cmt8v3v940001a4zc1b2v7nvx" || // hinankunren 1
        s.seasonId === "cmunx0pg90000mnzc5f0yg7iv" || // hinankunren 2
        s.seasonId === "cmuj8rted00010lnwciotguzf" || // saitama city
        s.seasonId === "cmunxinty000079zc6zf595vs"
      ;
      const isTepcoOnlyExcludedSeason =
        isTepcoPage && s.seasonId === REGULAR_SEASON_IDS[1];

      const nowJST = moment().tz("Asia/Tokyo");
      const seasonStartJST = moment(s.seasonStart).tz("Asia/Tokyo");

      if (
        !isExcludedSeasonId &&
        !isTepcoOnlyExcludedSeason &&
        seasonStartJST.isSameOrBefore(nowJST)
      ) {
        const seasonObj = {
          seasonId: s.seasonId,
          seasonName: s.seasonName,
          seasonStart: seasonStartJST.format("YYYY-MM-DD HH:mm"),
          seasonEnd: moment(s.seasonEnd).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm"),
        };
        seasons.push(seasonObj);
      }
    });
    yield put(setSeasonData(seasons));
  } catch (error) {
    console.log(error);
  }
}

export function* handleSeasonDataNtt() {
  try {
    const jwtToken = yield call(getJwtToken);
    const seasonDataCorrection = [
      {
        "seasonId": REGULAR_SEASON_IDS[2],
        "seasonName": "レギュラーシーズン3",
        "seasonStart": "2026-09-30T21:15:00.000Z",
        "seasonEnd": "2026-11-29T08:00:00.000Z",
      },
      {
        "seasonId": "cmr0bus0w00010azches0f7ak",
        "seasonName": "ピクトレまちバトル in 北海道 2026夏",
        "seasonStart": "2026-08-07T20:00:00.000Z",
        "seasonEnd": "2026-11-03T08:00:00.000Z"
      },
      {
        "seasonId": REGULAR_SEASON_IDS[1],
        "seasonName": "レギュラーシーズン2",
        "seasonStart": "2026-07-31T20:30:00.000Z",
        "seasonEnd": "2026-09-29T09:30:00.000Z"
      },
      {
        "seasonId": REGULAR_SEASON_IDS[0],
        "seasonName": "レギュラーシーズン1",
        "seasonStart": "2026-06-05T20:00:00.000Z",
        "seasonEnd": "2026-07-30T09:45:00.000Z",
      },
      {
        "seasonId": "cmkdvml9l0001ngs48zepg7ne",
        "seasonName": "東京・神奈川・千葉シーズン",
        "seasonStart": "2026-01-30T22:00:00.000Z",
        "seasonEnd": "2026-03-08T08:00:00.000Z",
      },
      {
        "seasonId": "cmg7iw3vc00037bowekjy65pc",
        "seasonName": "栃木シーズン",
        "seasonStart": "2025-10-11T00:00:00.000Z",
        "seasonEnd": "2025-11-30T07:15:00.000Z"
      },
      {
        "seasonId": "cmg7iw3va00017bow596s39cs",
        "seasonName": "長野・山梨・埼玉シーズン",
        "seasonStart": "2025-10-11T00:00:00.000Z",
        "seasonEnd": "2025-11-30T07:30:00.000Z"
      },
      {
        "seasonId": "cmb68qvei00011vo0e591229z",
        "seasonName": "北海道シーズン",
        "seasonStart": "2025-08-09T00:00:00.000Z",
        "seasonEnd": "2025-10-12T08:00:00.000Z",
      },
      {
        "seasonId": "cmd5oyfft0001iho10o2iceha",
        "seasonName": "岩手・宮城・福島・新潟シーズン",
        "seasonStart": "2025-07-25T20:30:00.000Z",
        "seasonEnd": "2025-09-15T09:30:00.000Z",
      },
      {
        "seasonId": "cmadtlnih0001n3mx1wvl9zog",
        "seasonName": "青森・秋田・山形シーズン",
        "seasonStart": "2025-05-16T22:00:00.000Z",
        "seasonEnd": "2025-06-22T09:00:00.000Z"
      },
      {
        "seasonId": "cm64mqm94000br3n0esu8c0n4",
        "seasonName": "北関東シーズン 2025",
        "seasonStart": "2025-02-01T22:00:00.000Z",
        "seasonEnd": "2025-03-16T07:00:00.000Z"
      },
      {
        "seasonId": "cm46zidp30003bpp6cxq101mo",
        "seasonName": "秋田県北 & 県央・県南シーズン",
        "seasonStart": "2024-12-06T23:00:00.000Z",
        "seasonEnd": "2025-01-12T07:00:00.000Z"
      },
    ];

    let seasons = [];
    let seasonObj = {};

    for (const s of seasonDataCorrection) {
      let seasonEnd = moment(s.seasonEnd).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");

      if (AREA_BATCH_SUPPORTED_SEASON_IDS.has(s.seasonId)) {
        const seasonMaxDateQuery = {
          query: `
            SELECT MAX(snapshotDateJst) AS maxSnapshotDateJst
            FROM ${REGULAR_SEASON_AREA_SNAPSHOT_TABLE}
            WHERE seasonId = '${s.seasonId}'
              AND companyCode = 'NTT_ME';
          `,
          database: "KpiDashboard",
          type: "list",
        };

        try {
          const snapshotMaxResp = yield call(
            getDataFromRDS,
            seasonMaxDateQuery,
            ENDPOINT.GET_DATA_RDS,
            jwtToken
          );
          const maxSnapshotDateJst = getFirstRowValue(snapshotMaxResp);
          if (maxSnapshotDateJst) {
            seasonEnd = moment(maxSnapshotDateJst)
              .tz("Asia/Tokyo")
              .endOf("day")
              .format("YYYY-MM-DD HH:mm");
          }
        } catch (error) {
          console.log(
            `[handleSeasonDataNtt] failed to fetch max date for ${s.seasonId}`,
            error
          );
        }
      }

      seasonObj.seasonId = s.seasonId;
      seasonObj.seasonName = s.seasonName;
      seasonObj.seasonStart = moment(s.seasonStart).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasonObj.seasonEnd = seasonEnd;
      seasons.push(seasonObj);
      seasonObj = {};
    }
    yield put(setSeasonDataNtt(seasons));
  } catch (error) {
    console.log(error);
  }
}

export function* handleSeasonDataNttFt() {
  try {
    const seasonDataCorrection = [
      {
        "seasonId": "cmkdxxnxn000141s4asb2gdfv",
        "seasonName": "沖縄シーズン",
        "seasonStart": "2026-01-31T22:00:00.000Z",
        "seasonEnd": "2026-03-22T09:00:00.000Z"
      },
    ];

    let seasons = [], seasonObj = {};
    _.each(seasonDataCorrection, function(s) {
      seasonObj.seasonId = s.seasonId;
      seasonObj.seasonName = s.seasonName;
      seasonObj.seasonStart = moment(s.seasonStart).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasonObj.seasonEnd = moment(s.seasonEnd).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasons.push(seasonObj);
      seasonObj = {};
    });
    yield put(setSeasonDataNttFt(seasons));
  } catch (error) {
    console.log(error);
  }
}


export function* handleSeasonDataHokuden() {
  try {
    const jwtToken = yield call(getJwtToken);

    const seasonDataCorrection = [
      {
          "seasonId": "cmr0bus0w00010azches0f7ak",
          "seasonName": "ピクトレまちバトル in 北海道 2026夏",
          "seasonStart": "2026-08-07T20:00:00.000Z",
          "seasonEnd": "2026-11-03T08:00:00.000Z"
      },
      {
        "seasonId": "cmb68qvei00011vo0e591229z",
        "seasonName": "PicTréeまちバトル in 北海道 2025夏",
        "seasonStart": "2025-07-12T00:00:00.000Z",
        "seasonEnd": "2025-10-12T08:00:00.000Z",
      },
    ];

    let seasons = [], seasonObj = {};
    _.each(seasonDataCorrection, function(s) {
      seasonObj.seasonId = s.seasonId;
      seasonObj.seasonName = s.seasonName;
      seasonObj.seasonStart = moment(s.seasonStart).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasonObj.seasonEnd = moment(s.seasonEnd).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasons.push(seasonObj);
      seasonObj = {};
    });
    yield put(setSeasonDataHokuden(seasons));
  } catch (error) {
    console.log(error);
  }
}

export function* handleSeasonDataCommon() {
  try {
    const seasonDataCorrection = [
      {
        "seasonId": "cmunx0pg90000mnzc5f0yg7iv",
        "seasonName": "防災チャンピオンシップ 全国 避難訓練シーズン2",
        "seasonStart": "2026-09-30T21:00:00.000Z",
        "seasonEnd": "2026-10-31T10:00:00.000Z",
      },
      {
        "seasonId": "cmt8v3v940001a4zc1b2v7nvx",
        "seasonName": "防災チャンピオンシップ 全国 避難訓練シーズン1",
        "seasonStart": "2026-08-31T21:00:00.000Z",
        "seasonEnd": "2026-09-30T10:00:00.000Z"
      },
    ];

    let seasons = [], seasonObj = {};
    _.each(seasonDataCorrection, function(s) {
      seasonObj.seasonId = s.seasonId;
      seasonObj.seasonName = s.seasonName;
      seasonObj.seasonStart = moment(s.seasonStart).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasonObj.seasonEnd = moment(s.seasonEnd).tz("Asia/Tokyo").format("YYYY-MM-DD HH:mm");
      seasons.push(seasonObj);
      seasonObj = {};
    });
    yield put(setSeasonDataCommon(seasons));
  } catch (error) {
    console.log(error);
  }
}

export default function* watchMapSettings() {
  yield takeEvery(PICTREE.GET_ALL_SEASON_DATA, handleAllSeasonData);
  yield takeEvery(PICTREE.GET_SEASON_DATA, handleSeasonData);
  yield takeEvery(PICTREE.GET_SEASON_DATA_NTT, handleSeasonDataNtt);
  yield takeEvery(PICTREE.GET_SEASON_DATA_NTT_FT, handleSeasonDataNttFt);
  yield takeEvery(PICTREE.GET_SEASON_DATA_HOKUDEN, handleSeasonDataHokuden);
  yield takeEvery(PICTREE.GET_SEASON_DATA_COMMON, handleSeasonDataCommon);
}
